import {ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {BusinessStockDto, TradeRecordDto, TradeTransformRequest} from '../../../../models/generated-models';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {DEFAULT_TRADE_UNIT, TRADE_BASE} from '../trade.constants';
import {formatQuantityWithUnit, normalizeQuantityInput, parseQuantity} from '../trade-utils';

/**
 * Processes the selected business's own stock into a new product lot
 * (e.g. 100 kg of overripe durian → 20 kg of paste).
 */
@Component({
  selector: 'app-trade-transform',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './trade-transform.component.html',
  styleUrl: './trade-transform.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeTransformComponent implements OnInit {
  private readonly api = inject(TradeApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  protected readonly store = inject(TradeStore);

  protected readonly stock = signal<BusinessStockDto[]>([]);
  protected readonly productNameOptions = signal<string[]>([]);
  protected readonly saving = signal(false);
  protected readonly submitted = signal(false);

  protected readonly inputProductName = signal('');

  /** The only unit in the system — shown, never typed. */
  protected readonly unit = DEFAULT_TRADE_UNIT;

  protected form = {
    inputQuantity: '',
    outputProductName: '',
    outputQuantity: ''
  };

  // Signal mirrors so the validity computeds re-evaluate (zoneless).
  protected readonly inputQuantityValue = signal(0);
  protected readonly outputQuantityValue = signal(0);
  protected readonly hasOutputProductName = signal(false);

  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;

  /** Pooled stock of the chosen input product/unit for the selected business. */
  protected readonly availableStock = computed(() => {
    const productName = this.inputProductName();
    const match = this.stock().find((item) => item.productName === productName);
    return parseQuantity(match?.quantity);
  });

  protected readonly inputExceedsStock = computed(
    () => this.inputQuantityValue() > this.availableStock()
  );

  /** Processing only loses weight; the output can never exceed what went in. */
  protected readonly outputExceedsInput = computed(
    () => this.outputQuantityValue() > this.inputQuantityValue()
  );

  protected readonly canSubmit = computed(() =>
    this.store.selectedBusinessId() != null
    && !!this.inputProductName()
    && this.inputQuantityValue() > 0
    && !this.inputExceedsStock()
    && this.hasOutputProductName()
    && this.outputQuantityValue() > 0
    && !this.outputExceedsInput()
  );

  constructor() {
    // Zoneless: the stock (and its product names) follows the selected business.
    effect(() => {
      const businessId = this.store.selectedBusinessId();
      if (businessId == null) {
        this.stock.set([]);
        return;
      }
      this.api.listStock(businessId).subscribe({
        next: (items) => this.stock.set((items ?? []).filter((item) => Number(item.quantity ?? 0) > 0)),
        error: () => this.stock.set([])
      });
    });
  }

  ngOnInit(): void {
    // `unit` may still arrive from an old bookmark — it is simply ignored.
    this.inputProductName.set(this.route.snapshot.queryParamMap.get('productName')?.trim() ?? '');

    void this.store.loadBusinesses();
    this.loadProductNames();
  }

  /** Output name suggestions: the business's own stock names plus known products. */
  protected readonly outputNameSuggestions = computed(() => {
    const names = [
      ...this.stock().map((item) => item.productName?.trim()),
      ...this.productNameOptions()
    ].filter((name): name is string => !!name);
    return [...new Set(names)];
  });

  /** Convenience: process the whole pool. */
  protected useAllStock(): void {
    this.form.inputQuantity = String(this.availableStock());
    this.onInputQuantityInput();
  }

  protected onInputQuantityInput(): void {
    this.form.inputQuantity = normalizeQuantityInput(this.form.inputQuantity);
    this.inputQuantityValue.set(parseQuantity(this.form.inputQuantity));
  }

  protected onOutputQuantityInput(): void {
    this.form.outputQuantity = normalizeQuantityInput(this.form.outputQuantity);
    this.outputQuantityValue.set(parseQuantity(this.form.outputQuantity));
  }

  protected onOutputProductNameInput(): void {
    this.hasOutputProductName.set(!!this.form.outputProductName.trim());
  }

  protected submit(): void {
    this.submitted.set(true);
    if (this.saving() || !this.canSubmit()) {
      return;
    }
    const inputQuantity = this.inputQuantityValue();
    const outputQuantity = this.outputQuantityValue();

    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการแปรรูป',
      content: `ใช้ ${formatQuantityWithUnit(inputQuantity, this.unit)} `
        + `${this.inputProductName()} แปรรูปเป็น `
        + `${formatQuantityWithUnit(outputQuantity, this.unit)} `
        + `${this.form.outputProductName} หรือไม่`,
      confirmButtonMsg: 'ยืนยัน',
      closeButtonMsg: 'ยกเลิก'
    })).then((confirmed) => {
      if (confirmed) {
        this.save(inputQuantity, outputQuantity);
      }
    }).catch(() => undefined);
  }

  private save(inputQuantity: number, outputQuantity: number): void {
    this.saving.set(true);
    const request: TradeTransformRequest = {
      businessId: this.store.selectedBusinessId() ?? undefined,
      inputProductName: this.inputProductName(),
      inputQuantity,
      outputProductName: this.form.outputProductName.trim(),
      outputQuantity
    };
    this.api.transform(request).subscribe({
      next: (saved: TradeRecordDto) => {
        this.saving.set(false);
        // The result of a transform is a new pool, so land on the stock with that card
        // flagged rather than on the record's journey page.
        const product = saved.productName?.trim() || request.outputProductName;
        const quantity = saved.quantity ?? outputQuantity;
        this.toast.success(
          `แปรรูปแล้ว เพิ่ม ${product} ${formatQuantityWithUnit(quantity, DEFAULT_TRADE_UNIT)} เข้าสต็อก`
        );
        void this.router.navigate([TRADE_BASE], {state: {addedStock: [{productName: product, quantity}]}});
      },
      error: () => this.saving.set(false)
    });
  }

  private loadProductNames(): void {
    this.api.listProductNames().subscribe({
      next: (names) => this.productNameOptions.set(
        (names ?? []).map((name) => name?.trim()).filter((name): name is string => !!name)
      ),
      error: () => this.productNameOptions.set([])
    });
  }
}
